/* istanbul ignore file: https://clarifai.atlassian.net/browse/LT-2756 */
import { map } from 'rxjs/operators';
import { pipe } from 'fp-ts/lib/function';
import { isEmpty } from 'rambda';
import { from, Observable } from 'rxjs';
import { either } from 'fp-ts';
import { ReactLeft, UI_STATES } from 'utils/uiStates/uiStates';
import { errorToReactLeft, makeLeftEncoder, ReactLeftError } from 'utils/fp';
import type { QueryClient } from 'react-query';
import { fetchQueryTE, queryKeys } from 'utils/react-query';
import { postModelPredictionTE } from 'api/models/predict';
import { PredictionListMSTLeft } from 'modules/Predictions/mst/basePredictionStore';
import { postWorkflowPredictionTE } from 'api/workflows/predict';
import { TaskEither } from 'fp-ts/lib/TaskEither';
import { StatusCode } from 'api/grpc/resources';

type LeftT = PredictionListMSTLeft<CF.API.Models.Model>;

export type CachedPostModelPredictBody = CF.API.ModelOutputs.PostModelTextBody & { cacheKey: string };
/** takes in model and payload stream; returns stream of modelOutputs (in Either)  */
export const makeModelPredict$ = ({
  model,
  getVersionId,
  queryClient,
  getDeploymentId,
}: {
  model: CF.API.Models.Model;
  queryClient: QueryClient;
  getVersionId: () => string | undefined;
  getDeploymentId: () => string | undefined;
}) => {
  return (body: CF.API.ModelOutputs.PostModelPredictBody): Observable<either.Either<ReactLeft<LeftT>, CF.API.ModelOutputs.ModelOutput>> => {
    const {
      id: modelId,
      app_id: appId,
      user_id: userId,
      model_version: { id: defaultModelVersionId },
    } = model;
    const modelVersionId = getVersionId() || defaultModelVersionId;
    const deploymentId = getDeploymentId();

    // TODO: subtle bug here: we store "presets" on the model, but predictions come from whichever model version is the latest at the time
    // of caching the predictions. This means that if a user changes the model version, the predictions will be stale. One potential fix is to
    // automatically update the stored predictions when the owner re-trains the model
    const cachedOutputs = findCachedOutputs(model, body);

    const runnerSelector = {
      deployment: {
        id: deploymentId || '',
        user_id: userId,
      },
    };

    // if cacheKey is sent, cache will based on that key instead
    const { cacheKey, ...postBody } = body as CachedPostModelPredictBody;

    // if cached outputs are found, put those into a TaskEither, otherwise, make the API call
    const queryFn: TaskEither<
      ReactLeftError<Error | CF.API.ModelOutputs.ModelOutputsResponse>,
      CF.API.ModelOutputs.ModelOutputsResponse
    > = cachedOutputs
      ? taskEitherFromCachedOutputsTE(cachedOutputs)
      : postModelPredictionTE(
          {
            ...{ modelId, appId, userId, modelVersionId },
            body: { inputs: [{ data: postBody }], runner_selector: deploymentId ? runnerSelector : undefined },
          },
          errorToReactLeft,
        );

    return from(
      fetchQueryTE(
        queryClient,
        [
          cacheKey || queryKeys.ModelPredictions,
          {
            modelVersionId,
            modelId,
            appId,
            userId,
            body: postBody,
          },
        ],
        queryFn,
        {
          staleTime: 300000,
        },
      ),
    ).pipe(
      map((responseE) => {
        const modelOutput = pipe(
          responseE,
          either.mapLeft((error) =>
            /* istanbul ignore next: runtime type check to guard against browser-returned errors like connection error */
            error.props && error.props.errorResponse && 'status' in error.props.errorResponse
              ? {
                  ...error,
                  title: error.props.errorResponse.status?.description,
                  reason: '',
                }
              : error,
          ),
          either.map(
            /* istanbul ignore next: added a case with empty data object because API sends bad data sometimes */
            (result) => result.outputs[0] || ({ data: {} } as unknown as typeof result.outputs[0]),
          ),
        );

        const outputE = makeLeftEncoder<ReactLeft<LeftT>, CF.API.ModelOutputs.ModelOutput>((x: CF.API.ModelOutputs.ModelOutput) => isEmpty(x.data), {
          type: UI_STATES.empty,
          props: { reason: 'No detections from model.', title: 'Failure' },
        })(modelOutput);
        return outputE;
      }),
    );
  };
};

type WfLeftT = PredictionListMSTLeft<CF.API.Workflows.Workflow>;

export const makeWfPredict$ = ({
  workflow,
  getVersionId,
  queryClient,
}: {
  workflow: CF.API.Workflows.Workflow;
  queryClient: QueryClient;
  getVersionId: () => string | undefined;
}) => {
  return (
    body: CF.API.WorkflowOutputs.PostWorkflowPredictBody,
  ): Observable<either.Either<ReactLeft<WfLeftT>, CF.API.WorkflowOutputs.WorkflowOutput[]>> => {
    const {
      id: workflowId,
      app_id: appId,
      user_id: userId,
      version: { id: defaultWorkflowVersionId },
    } = workflow;
    const workflowVersionId = getVersionId() || defaultWorkflowVersionId;
    return from(
      fetchQueryTE(
        queryClient,
        [
          'WorkflowPredictions',
          {
            versionId: workflowVersionId,
            workflowId,
            appId,
            userId,
            body,
          },
        ],
        postWorkflowPredictionTE(
          {
            ...{
              workflowId,
              appId,
              userId,
              workflowVersionId,
            },
            body: { inputs: [{ data: body }] },
          },
          errorToReactLeft,
        ),
        {
          staleTime: 300000,
        },
      ),
    ).pipe(
      map((responseE) => {
        const wfOutput = pipe(
          responseE,
          either.mapLeft((error) =>
            /* istanbul ignore next: runtime type check to guard against browser-returned errors like connection error */
            error.props && 'status' in error.props.errorResponse
              ? {
                  ...error,
                  title: error.props.errorResponse.status.description,
                  reason: '',
                }
              : error,
          ),
          either.map(
            /* istanbul ignore next: added a case with empty data object because API sends bad data sometimes */
            (result) => result.results.map((x) => x.outputs).flat(),
          ),
        );

        const outputE = makeLeftEncoder<ReactLeft<WfLeftT>, CF.API.WorkflowOutputs.WorkflowOutput[]>((x) => isEmpty(x), {
          type: UI_STATES.empty,
          props: { reason: 'No detections from workflow.', title: 'Failure' },
        })(wfOutput);

        return outputE;
      }),
    );
  };
};

/**
 * Searches the model presets for a cached output that belongs to the given input body.
 * @param model The model to be searched
 * @param body The body that was requested
 * @returns Cached outputs if found, otherwise null
 */
function findCachedOutputs(model: CF.API.Models.Model, body: CF.API.ModelOutputs.PostModelPredictBody): CF.API.ModelOutputs.ModelOutput | null {
  if (!Array.isArray(model.presets?.outputs)) {
    return null;
  }

  const presets: CF.API.ModelOutputs.ModelPreset[] = model.presets?.outputs || [];
  const matchedPreset = presets.find((preset: CF.API.ModelOutputs.ModelPreset) => {
    if ('image' in preset.input.data && 'image' in body && 'url' in preset.input.data.image && 'url' in body.image) {
      return preset.input.data.image.url === body.image.url;
    }
    return false;
  });
  return matchedPreset !== undefined
    ? {
        id: matchedPreset.input.id,
        status: {
          code: StatusCode.SUCCESS,
          description: 'Cached prediction from model.',
        },
        created_at: new Date().toISOString(),
        model,
        input: matchedPreset.input,
        data: matchedPreset.data,
      }
    : null;
}

/**
 * Creates a TaskEither that returns the cached outputs.
 * @param outputs The cached outputs
 * @returns A TaskEither that resolves to the cached outputs
 */

function taskEitherFromCachedOutputsTE(
  outputs: CF.API.ModelOutputs.ModelOutput,
): TaskEither<ReactLeftError<Error | CF.API.ModelOutputs.ModelOutputsResponse>, CF.API.ModelOutputs.ModelOutputsResponse> {
  return () => {
    const response = {
      status: {
        code: StatusCode.SUCCESS,
        description: 'Cached prediction from model.',
      },
      outputs: [outputs],
    };

    return Promise.resolve(
      either.right(response) as either.Either<
        ReactLeftError<Error | CF.API.ModelOutputs.ModelOutputsResponse>,
        CF.API.ModelOutputs.ModelOutputsResponse
      >,
    );
  };
}
